import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Input, Textarea } from '@heroui/input';
import { Button } from '@heroui/button';
import { useMutation } from '@tanstack/react-query';
import { createContactUs } from '@/apiEndpoints';
import { addToast } from '@heroui/react';
import Location from '@/icons/Location';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileBottomNav from '@/components/MobileBottomNav';
import { IconMail as Mail, IconPhone as Phone } from '@tabler/icons-react';
import { PageHero, bodyMuted, btnAccent, card, cardTitle, eyebrow, pageBg, sectionTitle, surface, wrap } from '@/components/ui/design';

const fieldLabel = "font-semibold text-grey40 dark:text-nightMuted";
const fieldWrapper = "rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60";
const fieldInput = "text-grey10 dark:text-nightTextSecondary";
const submitClass = `${btnAccent} h-14 w-full text-base`;
const policyLink = "font-semibold text-primary40 underline-offset-4 hover:underline dark:text-nightAccent";

const ContactPage = () => {
    // Helper function to get site name from environment
    const getSiteName = () => {
        return (import.meta as any).env?.VITE_SITE_NAME || 'EventUp';
    };

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        message: ''
    });
    const [errors, setErrors] = useState({
        fullName: '',
        email: '',
        message: ''
    });


    // Email validation function
    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Form validation function
    const validateForm = () => {
        const newErrors = {
            fullName: '',
            email: '',
            message: ''
        };
        let isValid = true;

        // Validate full name
        if (!formData.fullName.trim()) {
            newErrors.fullName = 'Full name is required';
            isValid = false;
        } else if (formData.fullName.trim().length < 2) {
            newErrors.fullName = 'Full name must be at least 2 characters';
            isValid = false;
        }

        // Validate email
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
            isValid = false;
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
            isValid = false;
        }

        // Validate message
        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
            isValid = false;
        } else if (formData.message.trim().length < 10) {
            newErrors.message = 'Message must be at least 10 characters';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user starts typing
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const createContactUsMutation = useMutation({
        mutationFn: async (data: { fullName: string; email: string; message: string }) => {
            // Combine the form data into a single message body
            const messageBody = `Name: ${data.fullName}\nEmail: ${data.email}\nMessage: ${data.message}`;
            return createContactUs(messageBody);
        },
        onSuccess: (data) => {
            console.log('Contact form submitted successfully:', data);
            // Clear form data after successful submission
            setFormData({
                fullName: '',
                email: '',
                message: ''
            });
            // Clear errors
            setErrors({
                fullName: '',
                email: '',
                message: ''
            });
            // Show success toast
            addToast({
                title: 'Success!',
                description: 'Your message has been sent successfully. We\'ll get back to you soon!',
                color: 'success',
            });
        },
        onError: (error: any) => {
            console.error('Error submitting contact form:', error);
            // Show error toast
            addToast({
                title: 'Error',
                description: 'Failed to send your message. Please try again later.',
                color: 'danger',
            });
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form before submission
        if (!validateForm()) {
            addToast({
                title: 'Validation Error',
                description: 'Please fix the errors in the form before submitting.',
                color: 'warning',
            });
            return;
        }

        createContactUsMutation.mutate(formData);
    };

    return (
        <>
            <Helmet>
                <title>Contact Us - {getSiteName()}</title>
            </Helmet>

            <Header />

            <main className={`min-h-screen font-inter ${pageBg}`}>
                <PageHero
                    eyebrow="Contact Us"
                    title="Get In Touch"
                    subtitle={<>Email, call or complete the form to learn how {getSiteName()} can solve your problem.</>}
                />

                <section className={`${wrap} mt-12 sm:mt-16`}>
                    <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-col gap-3 sm:flex-row">
                                <div className={`${surface} flex flex-1 items-center gap-4 p-5`}>
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent">
                                        <Mail className="h-5 w-5" />
                                    </span>
                                    <span className="min-w-0 break-words text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white">admin@alimotech.com</span>
                                </div>
                                <a href="tel:9494316160" className={`${surface} flex flex-1 items-center gap-4 p-5 transition-colors hover:bg-grey95 dark:hover:bg-white/[0.08]`}>
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent">
                                        <Phone className="h-5 w-5" />
                                    </span>
                                    <span className="text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white">(949) 431-6160</span>
                                </a>
                            </div>

                            <div className="mt-10 grid gap-3 sm:grid-cols-3 lg:mt-14">
                                <div className={`${card} p-6`}>
                                    <h3 className={`${cardTitle} !text-lg mb-3`}>Customer support</h3>
                                    <p className="text-[15px] leading-relaxed text-grey40 dark:text-nightMuted">
                                        Our support team is available around the clock to address any concerns or queries you may have.
                                    </p>
                                </div>

                                <div className={`${card} p-6`}>
                                    <h3 className={`${cardTitle} !text-lg mb-3`}>Feedback & Suggestions</h3>
                                    <p className="text-[15px] leading-relaxed text-grey40 dark:text-nightMuted">
                                        We value your feedback and are continuously working to improve {getSiteName()}. Your input is crucial in shaping the future of {getSiteName()}.
                                    </p>
                                </div>

                                <div className={`${card} p-6`}>
                                    <h3 className={`${cardTitle} !text-lg mb-3`}>Media Inquiries</h3>
                                    <p className="break-words text-[15px] leading-relaxed text-grey40 dark:text-nightMuted">
                                        For media-related questions or press inquiries, please contact us at admin@nexalogix.com.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className={`hidden sm:block ${card} w-full p-8 lg:w-[464px] lg:flex-shrink-0`}>
                            <h2 className={`${eyebrow} mb-6`}>You can reach us anytime:</h2>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <Input
                                        label="Full Name"
                                        value={formData.fullName}
                                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                                        className="w-full"
                                        variant="flat"
                                        isInvalid={!!errors.fullName}
                                        errorMessage={errors.fullName}
                                        classNames={{
                                            label: fieldLabel,
                                            input: (errors.fullName ? "border-red-500 " : "") + fieldInput,
                                            inputWrapper: (errors.fullName ? "border-red-500 " : "") + fieldWrapper,
                                        }}
                                    />
                                </div>

                                <div>
                                    <Input
                                        label="Email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        className="w-full"
                                        variant="flat"
                                        isInvalid={!!errors.email}
                                        errorMessage={errors.email}
                                        classNames={{
                                            label: fieldLabel,
                                            input: (errors.email ? "border-red-500 " : "") + fieldInput,
                                            inputWrapper: (errors.email ? "border-red-500 " : "") + fieldWrapper,
                                        }}
                                    />
                                </div>

                                <div>
                                    <Textarea
                                        label="How can we help?"
                                        value={formData.message}
                                        onChange={(e) => handleInputChange('message', e.target.value)}
                                        className="w-full"
                                        variant="flat"
                                        minRows={4}
                                        isInvalid={!!errors.message}
                                        errorMessage={errors.message}
                                        classNames={{
                                            label: fieldLabel,
                                            input: (errors.message ? "border-red-500 " : "") + fieldInput,
                                            inputWrapper: (errors.message ? "border-red-500 " : "") + fieldWrapper,
                                        }}
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className={submitClass}
                                    disabled={createContactUsMutation.isPending}
                                >
                                    {createContactUsMutation.isPending ? 'Submitting...' : 'Submit'}
                                </Button>

                                <p className="text-center text-[13px] leading-relaxed text-grey40 dark:text-nightMuted">
                                    By contacting us you agree to our{' '}
                                    <Link to="/terms" className={policyLink}>Term of Use</Link>,{' '}
                                    <Link to="/privacy-policy" className={policyLink}>Privacy Policy</Link>.
                                </p>
                            </form>
                        </div>
                    </div>
                </section>
                <div className={`${wrap} mt-10`}>
                    <div className={`block sm:hidden ${card} w-full flex-shrink-0 p-6`}>
                        <h2 className={`${eyebrow} mb-6`}>You can reach us anytime:</h2>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <Input
                                    label="Full Name"
                                    value={formData.fullName}
                                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                                    className="w-full"
                                    variant="flat"
                                    isInvalid={!!errors.fullName}
                                    errorMessage={errors.fullName}
                                    classNames={{
                                        label: fieldLabel,
                                        input: (errors.fullName ? "border-red-500 " : "") + fieldInput,
                                        inputWrapper: (errors.fullName ? "border-red-500 " : "") + fieldWrapper,
                                    }}
                                />
                            </div>

                            <div>
                                <Input
                                    label="Email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    className="w-full"
                                    variant="flat"
                                    isInvalid={!!errors.email}
                                    errorMessage={errors.email}
                                    classNames={{
                                        label: fieldLabel,
                                        input: (errors.email ? "border-red-500 " : "") + fieldInput,
                                        inputWrapper: (errors.email ? "border-red-500 " : "") + fieldWrapper,
                                    }}
                                />
                            </div>

                            <div>
                                <Textarea
                                    label="How can we help?"
                                    value={formData.message}
                                    onChange={(e) => handleInputChange('message', e.target.value)}
                                    className="w-full"
                                    variant="flat"
                                    minRows={4}
                                    isInvalid={!!errors.message}
                                    errorMessage={errors.message}
                                    classNames={{
                                        label: fieldLabel,
                                        input: (errors.message ? "border-red-500 " : "") + fieldInput,
                                        inputWrapper: (errors.message ? "border-red-500 " : "") + fieldWrapper,
                                    }}
                                />
                            </div>

                            <Button
                                type="submit"
                                className={submitClass}
                                disabled={createContactUsMutation.isPending}
                            >
                                {createContactUsMutation.isPending ? 'Submitting...' : 'Submit'}
                            </Button>

                            <p className="text-center text-[13px] leading-relaxed text-grey40 dark:text-nightMuted">
                                By contacting us you agree to our{' '}
                                <Link to="/terms" className={policyLink}>Term of Use</Link>,{' '}
                                <Link to="/privacy-policy" className={policyLink}>Privacy Policy</Link>.
                            </p>
                        </form>
                    </div>
                </div>

                <section className={`${wrap} pt-24 pb-20 sm:pt-32`}>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-12">
                        <div className="lg:col-span-2">
                            <div className="relative h-80 overflow-hidden rounded-[28px] bg-grey99 ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10 sm:h-96">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d26430.393553120906!2d-118.43209796322542!3d34.09042349999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2bc04d6d147ab%3A0xd6c7c379fd081ed1!2sBeverly%20Hills%2C%20CA%2C%20USA!5e0!3m2!1sen!2s!4v1640995200000!5m2!1sen!2s"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                ></iframe>
                                <div className="absolute top-4 left-4 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg dark:bg-nightElevated">
                                    <Location className="text-primary w-6 h-6" />
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col justify-center space-y-6">
                            <div>
                                <h2 className={`${eyebrow} mb-4`}>Our Location</h2>
                                <h3 className={`${sectionTitle} mb-4`}>Connecting Near And Far</h3>
                            </div>

                            <div className={`space-y-2 ${bodyMuted}`}>
                                <div className="font-semibold text-grey10 dark:text-white">{getSiteName()}</div>
                                <div>Los Angeles, USA</div>
                                <div>Alimotech LLC</div>
                                <div>Aliso Viejo , CA 92656</div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer hasMobileNav />
            <MobileBottomNav />
        </>
    );
};

export default ContactPage;
